import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
    SwUpdate,
    VersionDetectedEvent,
    VersionInstallationFailedEvent,
    VersionReadyEvent,
} from '@angular/service-worker';
import { ToastService } from './toast.service';
import { isPlatformServer } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class SwUpdateService {
    private readonly _toastOptions = {
        sticky: true,
    };
    private _toastId = '';
    private _isServer = true;

    constructor(
        private readonly _swUpdate: SwUpdate,
        private readonly _toast: ToastService,
        @Inject(PLATFORM_ID) private readonly _platformId: object
    ) {
        this._isServer = isPlatformServer(this._platformId);
    }

    /**
     * Detect new version of the app
     *
     * @return {*}  {void}
     * @memberof SwUpdateService
     */
    public detectNewVersion(): void {
        if (this._isServer) {
            console.log('Service worker is not supported on server side');
            return;
        }

        if (!this._swUpdate.isEnabled) {
            console.log('Service worker is not enabled');
            return;
        }

        this._swUpdate.versionUpdates.subscribe((event) => {
            const versionUpdateMaps: Record<string, () => void> = {
                VERSION_DETECTED: () => this._versionDetected(event as VersionDetectedEvent),
                VERSION_READY: () => this._versionReady(event as VersionReadyEvent),
                VERSION_INSTALLATION_FAILED: () =>
                    this._versionInstallingFailed(event as VersionInstallationFailedEvent),
            };

            const fn = versionUpdateMaps[event.type];
            if (fn) {
                fn();
            }
        });
    }

    /**
     * Handle version detected event
     *
     * @private
     * @param {VersionDetectedEvent} event
     * @memberof SwUpdateService
     */
    private _versionDetected(event: VersionDetectedEvent): void {
        console.log(`Downloading new app version: ${event.version.hash}`);
        this._toastId = this._toast.info(
            $localize`:@@common.version-update.detect-title:Phát hiện phiên bản mới`,
            $localize`:@@common.version-update.detect-content:Đang tải phiên bản mới, vui lòng chờ...`,
            this._toastOptions
        );
    }

    /**
     * Handle version ready event
     *
     * @private
     * @param {VersionReadyEvent} event
     * @memberof SwUpdateService
     */
    private _versionReady(event: VersionReadyEvent): void {
        console.log(`Current app version: ${event.currentVersion.hash}`);
        console.log(`New app version ready for use: ${event.latestVersion.hash}`);
        if (this._toastId) {
            this._toast.clear(this._toastId);
        }
        const options = {
            ...this._toastOptions,
            action: {
                label: $localize`:@@common.version-update.buttons.reload:Tải lại`,
                fn: () => {
                    window.location.reload();
                },
            },
        };
        this._toast.success(
            $localize`:@@common.version-update.ready-title:Phiên bản mới đã sẵn sàng`,
            $localize`:@@common.version-update.ready-content:Vui lòng tải lại trang để cập nhật phiên bản mới`,
            options
        );
    }

    /**
     * Handle version installation failed event
     *
     * @private
     * @param {VersionInstallationFailedEvent} error
     * @memberof SwUpdateService
     */
    private _versionInstallingFailed(error: VersionInstallationFailedEvent): void {
        console.error('Version installation failed', error);
        this._toast.error(
            $localize`:@@common.version-update.install-failed-title:Kiểm tra phiên bản`,
            $localize`:@@common.version-update.install-failed-content:Không thể cài đặt phiên bản mới, vui lòng thử lại sau`,
            this._toastOptions
        );
    }
}
